import { AppState, Auth0ContextInterface, Auth0Provider, Auth0ProviderOptions, useAuth0 } from "@auth0/auth0-react";
import axios, { AxiosRequestHeaders } from "axios";
import { defer, isEmpty, omitBy } from "lodash-es";
import { useRouter } from "next/router";
import React, { useCallback, useEffect } from "react";
import { Deferred } from "ts-deferred";

const AUTH0_CONFIG: Auth0ProviderOptions = {
    domain: process.env.NEXT_PUBLIC_AUTH0_DOMAIN ?? "",
    clientId: process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID ?? "",
    authorizationParams: {
        audience: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE ?? "",
        redirect_uri: typeof window !== "undefined" ? window.location.origin : undefined,
    },
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    /**
     * @see https://github.com/auth0/auth0-react/blob/master/EXAMPLES.md#3-protecting-a-route-in-a-nextjs-app-in-spa-mode.
     */
    const router = useRouter();
    const handleRedirectCallback = useCallback(
        (appState?: AppState): void => {
            // Let children redirect first by sending this callback to the end of the call stack.
            defer(() => {
                // "/": This is the default route where you land, so let pages/index.tsx handle redirects.
                if (appState?.returnTo && appState.returnTo !== "/") {
                    router.replace(appState.returnTo);
                }
            });
        },
        [router],
    );

    return (
        <Auth0Provider {...AUTH0_CONFIG} onRedirectCallback={handleRedirectCallback}>
            <TokenResolvingProvider>{children}</TokenResolvingProvider>
        </Auth0Provider>
    );
};

const AUTHORIZATION_KEY = "Authorization";

export const NO_AUTH_HEADERS_INIT: Record<typeof AUTHORIZATION_KEY, string> = { [AUTHORIZATION_KEY]: "" };

const TokenResolvingProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { getAccessTokenSilently } = useAuth0();

    useEffect(() => {
        deferredGetAccessTokenSilently.resolve(getAccessTokenSilently);

        // Set axios defaults.
        const authInterceptor = axios.interceptors.request.use(async (config) => {
            const headers = config.headers ?? {};
            if (!(AUTHORIZATION_KEY in headers || AUTHORIZATION_KEY.toLowerCase() in headers)) {
                const token = await getAccessTokenSilently();
                headers[AUTHORIZATION_KEY] = `Bearer ${token}`;
            }

            return {
                ...config,
                // Empty header values are invalid, so we remove.
                // We have to cast as AxiosRequestHeaders because omitBy returns a Partial<AxiosRequestHeaders>
                // type which upsets the Axios typings
                headers: omitBy<AxiosRequestHeaders>(headers, isEmpty) as AxiosRequestHeaders,
            };
        });

        return () => axios.interceptors.request.eject(authInterceptor);
    }, [getAccessTokenSilently]);

    return <>{children}</>;
};

/**
 * Awaits auth before allowing us to get token.
 * @see https://github.com/auth0/auth0-react/issues/67 and @see https://gist.github.com/adamjmcgrath/0ed6a04047aad16506ca24d85f1b2a5c.
 */
const deferredGetAccessTokenSilently = new Deferred<Auth0ContextInterface["getAccessTokenSilently"]>();

export async function getAccessToken(): Promise<string> {
    const getToken = await deferredGetAccessTokenSilently.promise;
    return getToken();
}
